// src/pages/EventConnectSetup.tsx
// EventConnect, manager-facing event setup page (route /event-connect/setup/:eventId).
//
// Only usable by managers whose firm has been SELECTED for the event. The page
// is a 4-step workflow whose tabs unlock progressively:
//   1. Attendees     , always available; pick who from the firm attends.
//   2. Availability  , unlocks after ≥1 attendee saved; declare presentable rounds.
//   3. My Slot       , unlocks after PB assigns a slot; review investors, exclude, confirm.
//   4. My Schedule   , unlocks once the event schedule is published; read-only view.
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';
import { useProfile } from '../hooks/useProfile';
import {
  C, EventRow, Round, ManagerSlot, Attendee, PlenarySession, RoleAtEvent,
  ROLE_LABELS, SESSION_TYPES,
  fmtDate, fmtDateRange, fmtTimeRange, fmtTime, firstName,
} from '../lib/event';
import {
  card, inputStyle, labelStyle, btnPrimary, btnGold, btnGhost, btnDanger,
  PageHeader, StepTabs, Banner, LoadingScreen, EmptyState, StatusBadge, Pill, SectionLabel,
} from '../lib/eventUI';

type Tab = 'attendees' | 'availability' | 'slot' | 'schedule';

// ── Local (loosely typed) shapes for joined queries ──────────────────────────
interface TeamMember { id: string; name: string; title: string | null; }
interface ExternalAtt { name: string; email: string; title: string; }
interface SlotRow extends Omit<ManagerSlot, 'round'> { round?: Round | null; }
interface PrefRow {
  id: string;
  profile_id: string | null;
  preference_rank: number;
  profile?: { full_name: string } | null;
  investorFirm?: { name: string } | null;
}

const DEFAULT_ROUND_COUNT = 6;

export default function EventConnectSetup() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { firm, loading: profileLoading } = useProfile();

  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<Tab>('attendees');

  // Core event + this firm's registration.
  const [event, setEvent] = useState<EventRow | null>(null);
  const [role, setRole] = useState<RoleAtEvent | null>(null);
  const [isSelected, setIsSelected] = useState(false);

  // Tab 1, attendees.
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [savedAttendees, setSavedAttendees] = useState<Attendee[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<Set<string>>(new Set());
  const [externals, setExternals] = useState<ExternalAtt[]>([]);
  const [extName, setExtName] = useState('');
  const [extEmail, setExtEmail] = useState('');
  const [extTitle, setExtTitle] = useState('');
  const [attSaved, setAttSaved] = useState(false);

  // Tab 2, availability.
  const [rounds, setRounds] = useState<Round[]>([]);
  const [availRounds, setAvailRounds] = useState<number[]>([]);
  const [canMultiple, setCanMultiple] = useState(false);
  const [availSaved, setAvailSaved] = useState(false);

  // Tab 3, slot.
  const [slots, setSlots] = useState<SlotRow[]>([]);
  const [investorPrefs, setInvestorPrefs] = useState<PrefRow[]>([]);
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const [slotConfirmed, setSlotConfirmed] = useState(false);

  // Tab 4, schedule.
  const [plenary, setPlenary] = useState<PlenarySession[]>([]);

  const [saving, setSaving] = useState(false);

  // ── Initial load ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!profileLoading) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileLoading, eventId, firm?.id]);

  async function load() {
    if (!eventId) { setLoading(false); return; }
    setLoading(true);
    try {
      // Event row.
      const { data: ev } = await supabase.from('events').select('*').eq('id', eventId).single();
      setEvent((ev as EventRow) || null);

      // Without a firm the user cannot be a selected manager, bail early.
      if (!firm) { setIsSelected(false); setLoading(false); return; }

      // This firm's registration for the event (confirms selection + role).
      const { data: reg } = await supabase
        .from('event_registrations')
        .select('role_at_event, status')
        .eq('event_id', eventId)
        .eq('firm_id', firm.id)
        .maybeSingle();
      const selected = !!reg && (reg.status === 'selected' || reg.status === 'confirmed');
      setRole((reg?.role_at_event as RoleAtEvent) || null);
      setIsSelected(selected);
      if (!selected) { setLoading(false); return; }

      // Team members (name + title) used for the attendee selection list.
      const { data: tm } = await supabase
        .from('team_members')
        .select('id, name, title')
        .eq('firm_id', firm.id)
        .order('display_order', { nullsFirst: false })
        .order('id');
      const teamList = (tm as TeamMember[]) || [];
      setTeamMembers(teamList);

      // Existing saved attendees → pre-check team members (by name) + rebuild externals.
      const { data: att } = await supabase
        .from('event_attendees')
        .select('*')
        .eq('event_id', eventId)
        .eq('firm_id', firm.id);
      const savedList = (att as Attendee[]) || [];
      setSavedAttendees(savedList);
      const savedInternalNames = new Set(savedList.filter(a => !a.is_external).map(a => a.name));
      setSelectedTeam(new Set(teamList.filter(t => savedInternalNames.has(t.name)).map(t => t.id)));
      setExternals(savedList.filter(a => a.is_external).map(a => ({
        name: a.name, email: a.email || '', title: a.title || '',
      })));

      // Rounds (may or may not exist yet).
      const { data: rd } = await supabase
        .from('event_rounds')
        .select('*')
        .eq('event_id', eventId)
        .order('round_number');
      setRounds((rd as Round[]) || []);

      // Availability submission (pre-fill).
      const { data: av } = await supabase
        .from('event_manager_availability')
        .select('available_round_numbers, can_present_multiple_rounds')
        .eq('event_id', eventId)
        .eq('firm_id', firm.id)
        .maybeSingle();
      if (av) {
        setAvailRounds(Array.isArray(av.available_round_numbers) ? av.available_round_numbers : []);
        setCanMultiple(!!av.can_present_multiple_rounds);
      }

      // This firm's presenting slot(s), joined to the round for timing.
      const { data: sl } = await supabase
        .from('event_manager_slots')
        .select('*, round:event_rounds(*)')
        .eq('event_id', eventId)
        .eq('firm_id', firm.id);
      setSlots((sl as SlotRow[]) || []);

      // Investors who preferenced this manager's sessions.
      const { data: prefs } = await supabase
        .from('event_investor_preferences')
        .select('id, profile_id, preference_rank, profile:profiles(full_name), investorFirm:firms!event_investor_preferences_firm_id_fkey(name)')
        .eq('event_id', eventId)
        .eq('manager_firm_id', firm.id)
        .order('preference_rank');
      setInvestorPrefs((prefs as unknown as PrefRow[]) || []);

      // Manager-internal exclusions already recorded.
      const { data: exc } = await supabase
        .from('event_manager_exclusions')
        .select('excluded_profile_id')
        .eq('event_id', eventId)
        .eq('manager_firm_id', firm.id);
      setExcluded(new Set(((exc as { excluded_profile_id: string | null }[]) || [])
        .map(e => e.excluded_profile_id).filter((x): x is string => !!x)));

      // Plenary sessions (read-only timeline).
      const { data: pl } = await supabase
        .from('event_plenary_sessions')
        .select('*')
        .eq('event_id', eventId)
        .order('order_index')
        .order('start_time');
      setPlenary((pl as PlenarySession[]) || []);
    } catch {
      /* fetches degrade gracefully to their default empty state */
    }
    setLoading(false);
  }

  // Re-fetch just the saved attendee list (after a save) so locks/counts update.
  async function reloadAttendees() {
    if (!eventId || !firm) return;
    const { data } = await supabase
      .from('event_attendees')
      .select('*')
      .eq('event_id', eventId)
      .eq('firm_id', firm.id);
    setSavedAttendees((data as Attendee[]) || []);
  }

  // ── Derived workflow state ──────────────────────────────────────────────────
  const cap = event?.max_attendees_per_firm ?? 2;
  const totalSelected = selectedTeam.size + externals.length;
  const capReached = totalSelected >= cap;

  const attendeesSaved = savedAttendees.length > 0;
  const slotAssigned = slots.some(s => !!s.round_id && (s.status === 'ai_suggested' || s.status === 'confirmed'));
  const schedulePublished = !!event && ['published', 'live', 'completed'].includes(event.status);

  // Round numbers to show on the availability grid.
  const roundNumbers: number[] = rounds.length
    ? rounds.map(r => r.round_number)
    : Array.from({ length: DEFAULT_ROUND_COUNT }, (_, i) => i + 1);
  const roundByNumber = new Map(rounds.map(r => [r.round_number, r]));

  // ── Tab 1 handlers ──────────────────────────────────────────────────────────
  function toggleTeam(id: string) {
    setSelectedTeam(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (totalSelected < cap) next.add(id);
      return next;
    });
    setAttSaved(false);
  }

  function addExternal() {
    if (!extName.trim() || capReached) return;
    setExternals(prev => [...prev, { name: extName.trim(), email: extEmail.trim(), title: extTitle.trim() }]);
    setExtName(''); setExtEmail(''); setExtTitle('');
    setAttSaved(false);
  }

  function removeExternal(idx: number) {
    setExternals(prev => prev.filter((_, i) => i !== idx));
    setAttSaved(false);
  }

  async function saveAttendees() {
    if (!eventId || !firm) return;
    setSaving(true);
    try {
      // Simple replace strategy: clear then insert the current selection.
      await supabase.from('event_attendees').delete().eq('event_id', eventId).eq('firm_id', firm.id);
      const teamRows = teamMembers
        .filter(t => selectedTeam.has(t.id))
        .map(t => ({ event_id: eventId, firm_id: firm.id, is_external: false, name: t.name, title: t.title, profile_id: null }));
      const extRows = externals.map(e => ({
        event_id: eventId, firm_id: firm.id, is_external: true,
        name: e.name, email: e.email || null, title: e.title || null,
      }));
      const rows = [...teamRows, ...extRows];
      if (rows.length) await supabase.from('event_attendees').insert(rows);
      await reloadAttendees();
      setAttSaved(true);
    } catch {
      /* ignore, banner simply won't show */
    }
    setSaving(false);
  }

  // ── Tab 2 handlers ──────────────────────────────────────────────────────────
  function toggleRound(n: number) {
    setAvailRounds(prev => prev.includes(n) ? prev.filter(x => x !== n) : [...prev, n].sort((a, b) => a - b));
    setAvailSaved(false);
  }

  async function saveAvailability() {
    if (!eventId || !firm) return;
    setSaving(true);
    try {
      await supabase.from('event_manager_availability').upsert({
        event_id: eventId, firm_id: firm.id,
        available_round_numbers: availRounds,
        can_present_multiple_rounds: canMultiple,
      }, { onConflict: 'event_id,firm_id' });
      setAvailSaved(true);
    } catch { /* ignore */ }
    setSaving(false);
  }

  // ── Tab 3 handlers ──────────────────────────────────────────────────────────
  // Toggle a manager-internal exclusion (checkbox = INCLUDED). Never exposed to
  // the investor, this simply asks PB to reassign them elsewhere.
  async function toggleExclude(profileId: string) {
    if (!eventId || !firm) return;
    const isExcluded = excluded.has(profileId);
    // Optimistic UI update.
    setExcluded(prev => {
      const next = new Set(prev);
      if (isExcluded) next.delete(profileId); else next.add(profileId);
      return next;
    });
    try {
      if (isExcluded) {
        await supabase.from('event_manager_exclusions')
          .delete()
          .eq('event_id', eventId)
          .eq('manager_firm_id', firm.id)
          .eq('excluded_profile_id', profileId);
      } else {
        await supabase.from('event_manager_exclusions').insert({
          event_id: eventId, manager_firm_id: firm.id,
          excluded_profile_id: profileId, excluded_by: user?.id ?? null,
        });
      }
    } catch { /* keep optimistic state */ }
  }

  async function confirmSlot() {
    if (!eventId || !firm) return;
    setSaving(true);
    try {
      await supabase.from('event_manager_slots')
        .update({ status: 'confirmed' })
        .eq('event_id', eventId)
        .eq('firm_id', firm.id);
      setSlots(prev => prev.map(s => ({ ...s, status: 'confirmed' })));
      setSlotConfirmed(true);
    } catch { /* ignore */ }
    setSaving(false);
  }

  function exportInvestorList() {
    const data = investorPrefs.map(p => ({
      Name: p.profile?.full_name || '-',
      Firm: p.investorFirm?.name || '-',
      'Preference Rank': p.preference_rank,
      Status: p.profile_id && excluded.has(p.profile_id) ? 'Excluded' : 'Attending',
    }));
    const ws = XLSX.utils.json_to_sheet(data.length ? data : [{ Name: '', Firm: '', 'Preference Rank': '', Status: '' }]);
    ws['!cols'] = [{ wch: 26 }, { wch: 26 }, { wch: 16 }, { wch: 12 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Investors');
    XLSX.writeFile(wb, 'investor-list.xlsx');
  }

  // ── Render ──────────────────────────────────────────────────────────────────
  if (profileLoading || loading) return <LoadingScreen />;

  const backBtn = (
    <button onClick={() => navigate('/event-connect')} style={{ ...btnGhost, marginBottom: '18px' }}>← EventConnect</button>
  );

  // Gate: must be a selected manager for this event.
  if (!event || !firm || !isSelected) {
    return (
      <div style={{ padding: '28px 32px', minHeight: '100vh', background: C.surface, fontFamily: 'system-ui, -apple-system, sans-serif' }}>
        {backBtn}
        <Banner type="err" text="You are not a selected manager for this event." />
      </div>
    );
  }

  const subtitle = `${fmtDateRange(event.start_date, event.end_date)}${event.location ? ` · ${event.location}` : ''}`;

  const tabs = [
    { id: 'attendees' as Tab, label: 'Attendees' },
    { id: 'availability' as Tab, label: 'Availability', locked: !attendeesSaved },
    { id: 'slot' as Tab, label: 'My Slot', locked: !slotAssigned },
    { id: 'schedule' as Tab, label: 'My Schedule', locked: !schedulePublished },
  ];

  return (
    <div style={{ padding: '28px 32px', minHeight: '100vh', background: C.surface, fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      {backBtn}

      <PageHeader
        title={event.name}
        subtitle={subtitle}
        right={
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {role && <Pill text={ROLE_LABELS[role]} color={C.navy} bg="#EFF3FB" bd="#C7D6EE" />}
            <StatusBadge status={event.status} />
          </div>
        }
      />

      <StepTabs<Tab> tabs={tabs} active={tab} onChange={setTab} />

      <div style={{ maxWidth: '860px' }}>
        {tab === 'attendees' && (
          <AttendeesTab
            cap={cap}
            totalSelected={totalSelected}
            capReached={capReached}
            teamMembers={teamMembers}
            selectedTeam={selectedTeam}
            externals={externals}
            extName={extName} extEmail={extEmail} extTitle={extTitle}
            setExtName={setExtName} setExtEmail={setExtEmail} setExtTitle={setExtTitle}
            onToggleTeam={toggleTeam}
            onAddExternal={addExternal}
            onRemoveExternal={removeExternal}
            onSave={saveAttendees}
            saving={saving}
            saved={attSaved}
          />
        )}

        {tab === 'availability' && (
          <AvailabilityTab
            roundNumbers={roundNumbers}
            roundByNumber={roundByNumber}
            availRounds={availRounds}
            canMultiple={canMultiple}
            onToggleRound={toggleRound}
            onToggleMultiple={() => { setCanMultiple(v => !v); setAvailSaved(false); }}
            onSave={saveAvailability}
            saving={saving}
            saved={availSaved}
          />
        )}

        {tab === 'slot' && (
          <SlotTab
            slots={slots}
            investorPrefs={investorPrefs}
            excluded={excluded}
            onToggleExclude={toggleExclude}
            onConfirm={confirmSlot}
            onExport={exportInvestorList}
            saving={saving}
            confirmed={slotConfirmed || slots.some(s => s.status === 'confirmed')}
          />
        )}

        {tab === 'schedule' && (
          <ScheduleTab
            slots={slots}
            attendees={savedAttendees}
            plenary={plenary}
          />
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  TAB 1, ATTENDEES
// ════════════════════════════════════════════════════════════════════════════
function AttendeesTab(props: {
  cap: number; totalSelected: number; capReached: boolean;
  teamMembers: TeamMember[]; selectedTeam: Set<string>; externals: ExternalAtt[];
  extName: string; extEmail: string; extTitle: string;
  setExtName: (v: string) => void; setExtEmail: (v: string) => void; setExtTitle: (v: string) => void;
  onToggleTeam: (id: string) => void; onAddExternal: () => void; onRemoveExternal: (idx: number) => void;
  onSave: () => void; saving: boolean; saved: boolean;
}) {
  const {
    cap, totalSelected, capReached, teamMembers, selectedTeam, externals,
    extName, extEmail, extTitle, setExtName, setExtEmail, setExtTitle,
    onToggleTeam, onAddExternal, onRemoveExternal, onSave, saving, saved,
  } = props;

  return (
    <div>
      {saved && <Banner type="ok" text="Attendees saved. You can now declare your availability." />}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', gap: '12px', flexWrap: 'wrap' }}>
        <SectionLabel>Who from {teamMembers.length ? 'your firm' : 'your team'} is attending</SectionLabel>
        <span style={{ fontSize: '11.5px', fontWeight: 600, color: capReached ? C.amber : C.muted }}>
          {totalSelected} / {cap} selected · Max {cap} attendee{cap !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Team member selection list */}
      <div style={{ ...card, marginBottom: '20px' }}>
        {teamMembers.length === 0 ? (
          <div style={{ padding: '24px 18px', fontSize: '12.5px', color: C.mutedLt, textAlign: 'center' }}>
            No team members on file. Add external attendees below.
          </div>
        ) : teamMembers.map(t => {
          const checked = selectedTeam.has(t.id);
          const disabled = !checked && capReached;
          return (
            <label key={t.id} style={{
              display: 'flex', alignItems: 'center', gap: '12px', padding: '13px 16px',
              borderBottom: `1px solid ${C.borderLt}`, cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.5 : 1,
            }}>
              <input type="checkbox" checked={checked} disabled={disabled} onChange={() => onToggleTeam(t.id)} style={{ width: '16px', height: '16px', accentColor: C.navy }} />
              <div>
                <div style={{ fontSize: '13.5px', fontWeight: 500, color: C.text }}>{t.name}</div>
                {t.title && <div style={{ fontSize: '11.5px', color: C.muted, marginTop: '2px' }}>{t.title}</div>}
              </div>
            </label>
          );
        })}
      </div>

      {/* External attendees */}
      <SectionLabel style={{ marginBottom: '10px' }}>External attendees</SectionLabel>
      <div style={{ ...card, padding: '16px 18px', marginBottom: '20px' }}>
        {externals.length > 0 && (
          <div style={{ marginBottom: '14px' }}>
            {externals.map((e, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', borderBottom: i < externals.length - 1 ? `1px solid ${C.borderLt}` : 'none' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: 500, color: C.text }}>{e.name}</div>
                  <div style={{ fontSize: '11px', color: C.muted }}>
                    {[e.title, e.email].filter(Boolean).join(' · ') || 'External guest'}
                  </div>
                </div>
                <button onClick={() => onRemoveExternal(i)} style={{ ...btnDanger, padding: '5px 10px' }}>Remove</button>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
          <div>
            <label style={labelStyle}>Name</label>
            <input style={inputStyle} value={extName} onChange={e => setExtName(e.target.value)} placeholder="Full name" />
          </div>
          <div>
            <label style={labelStyle}>Title</label>
            <input style={inputStyle} value={extTitle} onChange={e => setExtTitle(e.target.value)} placeholder="e.g. Partner" />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Email</label>
            <input style={inputStyle} value={extEmail} onChange={e => setExtEmail(e.target.value)} placeholder="name@firm.com" />
          </div>
        </div>
        <button
          onClick={onAddExternal}
          disabled={!extName.trim() || capReached}
          style={{ ...btnGhost, marginTop: '12px', opacity: (!extName.trim() || capReached) ? 0.5 : 1, cursor: (!extName.trim() || capReached) ? 'not-allowed' : 'pointer' }}
        >
          + Add external attendee
        </button>
        {capReached && <div style={{ fontSize: '11px', color: C.amber, marginTop: '8px' }}>Attendee cap reached, remove someone to add another.</div>}
      </div>

      <button onClick={onSave} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}>
        {saving ? 'Saving…' : 'Save attendees'}
      </button>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  TAB 2, AVAILABILITY
// ════════════════════════════════════════════════════════════════════════════
function AvailabilityTab(props: {
  roundNumbers: number[]; roundByNumber: Map<number, Round>;
  availRounds: number[]; canMultiple: boolean;
  onToggleRound: (n: number) => void; onToggleMultiple: () => void;
  onSave: () => void; saving: boolean; saved: boolean;
}) {
  const { roundNumbers, roundByNumber, availRounds, canMultiple, onToggleRound, onToggleMultiple, onSave, saving, saved } = props;

  return (
    <div>
      {saved && <Banner type="ok" text="Your availability has been submitted. You will be notified when your slot is confirmed." />}

      <SectionLabel style={{ marginBottom: '10px' }}>Which rounds can you present in?</SectionLabel>
      <div style={{ ...card, padding: '18px', marginBottom: '18px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '10px' }}>
          {roundNumbers.map(n => {
            const rd = roundByNumber.get(n);
            const checked = availRounds.includes(n);
            return (
              <label key={n} style={{
                display: 'flex', alignItems: 'center', gap: '11px', padding: '12px 14px',
                border: `1px solid ${checked ? C.navy : C.border}`, borderRadius: '4px', cursor: 'pointer',
                background: checked ? '#F4F7FC' : C.white,
              }}>
                <input type="checkbox" checked={checked} onChange={() => onToggleRound(n)} style={{ width: '16px', height: '16px', accentColor: C.navy }} />
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: C.text }}>Round {n}</div>
                  <div style={{ fontSize: '11px', color: C.muted, marginTop: '2px' }}>
                    {rd ? `${fmtTimeRange(rd.start_time, rd.end_time)} · ${fmtDate(rd.start_time)}` : 'Time to be confirmed'}
                  </div>
                </div>
              </label>
            );
          })}
        </div>
      </div>

      {/* Multiple-rounds toggle */}
      <div style={{ ...card, padding: '14px 18px', marginBottom: '18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
        <div>
          <div style={{ fontSize: '13px', fontWeight: 600, color: C.text }}>Can present in multiple rounds if needed</div>
          <div style={{ fontSize: '11.5px', color: C.muted, marginTop: '2px' }}>Lets the scheduler seat you in more than one round to balance demand.</div>
        </div>
        <Toggle on={canMultiple} onClick={onToggleMultiple} />
      </div>

      <button onClick={onSave} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}>
        {saving ? 'Submitting…' : 'Submit availability'}
      </button>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  TAB 3, MY SLOT
// ════════════════════════════════════════════════════════════════════════════
function SlotTab(props: {
  slots: SlotRow[]; investorPrefs: PrefRow[]; excluded: Set<string>;
  onToggleExclude: (id: string) => void; onConfirm: () => void; onExport: () => void;
  saving: boolean; confirmed: boolean;
}) {
  const { slots, investorPrefs, excluded, onToggleExclude, onConfirm, onExport, saving, confirmed } = props;
  const presenting = slots.filter(s => !!s.round_id);

  return (
    <div>
      {confirmed && <Banner type="ok" text="Your slot is confirmed. Your attendee and exclusion selections are locked in." />}

      {/* Confirmed presenting round(s) */}
      <SectionLabel style={{ marginBottom: '10px' }}>Your presenting slot</SectionLabel>
      <div style={{ marginBottom: '22px' }}>
        {presenting.length === 0 ? (
          <EmptyState text="No slot has been assigned yet." />
        ) : presenting.map(s => (
          <div key={s.id} style={{ ...card, padding: '16px 18px', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
            <div style={{ minWidth: '150px' }}>
              <div style={{ fontFamily: 'Georgia, serif', fontSize: '18px', color: C.navy }}>
                {s.round ? fmtTimeRange(s.round.start_time, s.round.end_time) : 'Time TBC'}
              </div>
              <div style={{ fontSize: '11.5px', color: C.muted, marginTop: '2px' }}>
                {s.round ? fmtDate(s.round.start_time) : 'Round assignment pending'}
              </div>
            </div>
            <div style={{ flex: 1, minWidth: '120px' }}>
              <SectionLabel>Room / Booth</SectionLabel>
              <div style={{ fontSize: '14px', fontWeight: 600, color: C.text, marginTop: '4px' }}>{s.room || 'To be assigned'}</div>
            </div>
            <div>
              {s.status === 'confirmed'
                ? <Pill text="Confirmed" color={C.success} bg="#F0FDF4" bd="#BBF7D0" />
                : <Pill text="AI Suggested" color={C.amber} bg="#FEFCE8" bd="#FDE68A" />}
            </div>
          </div>
        ))}
      </div>

      {/* Investors who preferenced this manager */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px', gap: '12px', flexWrap: 'wrap' }}>
        <SectionLabel>Investors requesting your session ({investorPrefs.length})</SectionLabel>
        <button onClick={onExport} style={{ ...btnGhost }}>↓ Export investor list</button>
      </div>
      <div style={{ fontSize: '11.5px', color: C.muted, marginBottom: '10px', lineHeight: 1.5 }}>
        Uncheck an investor to have them reassigned away from your session. This is never shown to the investor.
      </div>

      <div style={{ ...card, marginBottom: '20px' }}>
        {investorPrefs.length === 0 ? (
          <div style={{ padding: '24px 18px', fontSize: '12.5px', color: C.mutedLt, textAlign: 'center' }}>
            No investors have preferenced your session yet.
          </div>
        ) : investorPrefs.map((p, i) => {
          const pid = p.profile_id || '';
          const isExcluded = !!pid && excluded.has(pid);
          return (
            <label key={p.id} style={{
              display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px',
              borderBottom: i < investorPrefs.length - 1 ? `1px solid ${C.borderLt}` : 'none',
              cursor: pid ? 'pointer' : 'default', opacity: isExcluded ? 0.45 : 1,
            }}>
              <input
                type="checkbox"
                checked={!isExcluded}
                disabled={!pid}
                onChange={() => pid && onToggleExclude(pid)}
                style={{ width: '16px', height: '16px', accentColor: C.navy }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '13.5px', fontWeight: 500, color: C.text, textDecoration: isExcluded ? 'line-through' : 'none' }}>
                  {p.profile?.full_name || 'Investor'}
                </div>
                <div style={{ fontSize: '11.5px', color: C.muted, marginTop: '2px' }}>{p.investorFirm?.name || '-'}</div>
              </div>
              {isExcluded && <span style={{ fontSize: '10.5px', color: C.amber, fontWeight: 600 }}>Will be reassigned</span>}
              <Pill text={`Rank ${p.preference_rank}`} color={C.textSec} bg={C.surface} bd={C.border} />
            </label>
          );
        })}
      </div>

      <button onClick={onConfirm} disabled={saving || presenting.length === 0} style={{ ...btnGold, opacity: (saving || presenting.length === 0) ? 0.6 : 1 }}>
        {saving ? 'Confirming…' : 'Confirm my slot'}
      </button>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  TAB 4, MY SCHEDULE (read-only)
// ════════════════════════════════════════════════════════════════════════════
function ScheduleTab(props: { slots: SlotRow[]; attendees: Attendee[]; plenary: PlenarySession[] }) {
  const { slots, attendees, plenary } = props;
  const presenting = slots.filter(s => !!s.round_id);
  const attendeeNames = attendees.map(a => firstName(a.name));
  const sessionLabel = (t: string) => SESSION_TYPES.find(s => s.value === t)?.label || t;

  return (
    <div>
      <SectionLabel style={{ marginBottom: '10px' }}>Your presenting session{presenting.length !== 1 ? 's' : ''}</SectionLabel>
      <div style={{ marginBottom: '26px' }}>
        {presenting.length === 0 ? (
          <EmptyState text="No presenting session on your schedule." />
        ) : presenting.map(s => (
          <TimelineCard
            key={s.id}
            time={s.round ? fmtTime(s.round.start_time) : '-'}
            dateLabel={s.round ? fmtDate(s.round.start_time) : ''}
            title={s.room ? `Presenting · ${s.room}` : 'Presenting session'}
            subtitle={
              <>
                {s.round ? `${fmtTimeRange(s.round.start_time, s.round.end_time)} · ` : ''}
                {attendees.length} attendee{attendees.length !== 1 ? 's' : ''} of {s.capacity} capacity
                {attendeeNames.length > 0 && <div style={{ marginTop: '4px', color: C.muted }}>{attendeeNames.join(', ')}</div>}
              </>
            }
          />
        ))}
      </div>

      <SectionLabel style={{ marginBottom: '10px' }}>Plenary programme</SectionLabel>
      <div>
        {plenary.length === 0 ? (
          <EmptyState text="No plenary sessions have been published." />
        ) : plenary.map(p => (
          <TimelineCard
            key={p.id}
            time={fmtTime(p.start_time)}
            dateLabel={fmtDate(p.start_time)}
            title={p.title}
            subtitle={
              <>
                {sessionLabel(p.session_type)} · {fmtTimeRange(p.start_time, p.end_time)}
                {p.location ? ` · ${p.location}` : ''}
              </>
            }
          />
        ))}
      </div>
    </div>
  );
}

// ── Small shared presentational bits ─────────────────────────────────────────
function Toggle({ on, onClick }: { on: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        width: '42px', height: '24px', borderRadius: '12px', border: 'none', cursor: 'pointer',
        background: on ? C.navy : C.border, position: 'relative', flexShrink: 0, transition: 'background 0.15s',
      }}
      aria-pressed={on}
    >
      <span style={{
        position: 'absolute', top: '3px', left: on ? '21px' : '3px', width: '18px', height: '18px',
        borderRadius: '50%', background: C.white, transition: 'left 0.15s', boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
      }} />
    </button>
  );
}

// Timeline card: left time column, gold bar, title/subtitle.
function TimelineCard({ time, dateLabel, title, subtitle }: { time: string; dateLabel?: string; title: string; subtitle: React.ReactNode }) {
  return (
    <div style={{ ...card, padding: '0', marginBottom: '10px', display: 'flex', overflow: 'hidden' }}>
      <div style={{ width: '92px', flexShrink: 0, padding: '14px 12px', background: C.surface, borderRight: `1px solid ${C.borderLt}`, textAlign: 'center' }}>
        <div style={{ fontFamily: 'Georgia, serif', fontSize: '15px', color: C.navy }}>{time}</div>
        {dateLabel && <div style={{ fontSize: '10px', color: C.muted, marginTop: '2px' }}>{dateLabel}</div>}
      </div>
      <div style={{ width: '3px', background: C.gold, flexShrink: 0 }} />
      <div style={{ padding: '13px 16px', minWidth: 0 }}>
        <div style={{ fontSize: '13.5px', fontWeight: 600, color: C.text }}>{title}</div>
        <div style={{ fontSize: '11.5px', color: C.textSec, marginTop: '3px', lineHeight: 1.5 }}>{subtitle}</div>
      </div>
    </div>
  );
}
